import { useLayoutEffect, useRef, type ReactNode } from "react";
import { X, ChevronRight } from "lucide-react";
let pointerTrigger: HTMLElement | null = null,
  pointerAt = 0;
document.addEventListener(
  "pointerdown",
  (e) => {
    pointerTrigger =
      e.target instanceof Element
        ? e.target.closest<HTMLElement>("button,a,input,select,summary")
        : null;
    pointerAt = performance.now();
  },
  true,
);
export function IconButton({
  label,
  children,
  onClick,
  pressed,
}: {
  label: string;
  children: ReactNode;
  onClick: () => void;
  pressed?: boolean;
}) {
  return (
    <button
      className="icon-button"
      type="button"
      aria-label={label}
      aria-pressed={pressed}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
export function Dialog({
  title,
  children,
  onClose,
  wide = false,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useLayoutEffect(() => {
    const previous =
      pointerTrigger?.isConnected && performance.now() - pointerAt < 1000
        ? pointerTrigger
        : (document.activeElement as HTMLElement);
    const el = ref.current!;
    el.showModal();
    const initial = el.querySelector<HTMLElement>(
      'input:not([type="checkbox"]), textarea, select',
    );
    initial?.focus({ preventScroll: true });
    return () => {
      el.close();
      requestAnimationFrame(() => {
        if (previous?.isConnected) previous.focus({ preventScroll: true });
      });
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className={wide ? "dialog wide" : "dialog"}
      aria-labelledby="dialog-title"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          const r = e.currentTarget.getBoundingClientRect();
          if (
            e.clientX < r.left ||
            e.clientX > r.right ||
            e.clientY < r.top ||
            e.clientY > r.bottom
          )
            onClose();
        }
      }}
    >
      <header className="dialog-header">
        <h2 id="dialog-title">{title}</h2>
        <IconButton label="Pencereyi kapat" onClick={onClose}>
          <X size={21} />
        </IconButton>
      </header>
      {children}
    </dialog>
  );
}
export function Empty({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty-mark" aria-hidden="true">
        Aa
      </div>
      <h2>{title}</h2>
      <p>{children}</p>
      {action}
    </div>
  );
}
export function Stat({
  label,
  value,
  detail,
  onClick,
}: {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  onClick?: () => void;
}) {
  const body = (
    <>
      <span className="muted">{label}</span>
      <strong>{value}</strong>
      {detail && <small>{detail}</small>}
      {onClick && (
        <ChevronRight className="stat-chevron" size={16} aria-hidden="true" />
      )}
    </>
  );
  return onClick ? (
    <button className="stat" onClick={onClick}>
      {body}
    </button>
  ) : (
    <div className="stat">{body}</div>
  );
}
export function Panel({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className="panel">
      <div className="section-heading">
        <h2>{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}
export function Toggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="setting-row">
      <span>
        <strong>{label}</strong>
        {description && <small>{description}</small>}
      </span>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
    </label>
  );
}
